/**
 * GrocerySettings — dedicated grocery Settings tab.
 *
 * Merges TWO backend endpoints:
 *   • /api/grocery/b/{slug}/settings      → tax + receipts + registers (legacy)
 *   • /api/grocery/b/{slug}/preferences   → alerts + notifications + sync (new)
 *
 * We render them as one cohesive UI so the merchant doesn't know two
 * endpoints exist. Save button PATCHes only the sections that changed.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Clock, DollarSign, AlertTriangle, Mail, MessageSquare, Unplug, Save, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GrocerySettings({ businessSlug, isDark }) {
  const [settings, setSettings] = useState(null);      // /settings response (tax etc)
  const [prefs, setPrefs] = useState(null);            // /preferences response (alerts etc)
  const [initialSettings, setInitialSettings] = useState(null);
  const [initialPrefs, setInitialPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/grocery/b/${businessSlug}/settings`),
        axios.get(`${API}/grocery/b/${businessSlug}/preferences`),
      ]);
      setSettings(s.data);
      setInitialSettings(JSON.parse(JSON.stringify(s.data)));
      setPrefs(p.data);
      setInitialPrefs(JSON.parse(JSON.stringify(p.data)));
    } catch (e) {
      toast.error("Could not load settings");
    }
    setLoading(false);
  }, [businessSlug]);

  useEffect(() => { load(); }, [load]);

  const settingsDirty = JSON.stringify(settings) !== JSON.stringify(initialSettings);
  const prefsDirty = JSON.stringify(prefs) !== JSON.stringify(initialPrefs);
  const dirty = settingsDirty || prefsDirty;

  const save = async () => {
    setSaving(true);
    try {
      const calls = [];
      if (settingsDirty) {
        calls.push(axios.post(`${API}/grocery/b/${businessSlug}/settings`, settings));
      }
      if (prefsDirty) {
        // Only send the sections we actually changed to keep PATCH small
        const diff = {};
        for (const k of Object.keys(prefs)) {
          if (JSON.stringify(prefs[k]) !== JSON.stringify(initialPrefs[k])) {
            diff[k] = prefs[k];
          }
        }
        calls.push(axios.patch(`${API}/grocery/b/${businessSlug}/preferences`, diff));
      }
      await Promise.all(calls);
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const disconnectClover = async (wipe = false) => {
    if (!window.confirm(wipe
      ? "This will disconnect Clover AND permanently delete all Clover-synced receipts + inventory. Are you sure?"
      : "Disconnect Clover from this business? Historical data will be kept — you can reconnect any time."
    )) return;
    setDisconnecting(true);
    try {
      const r = await axios.post(
        `${API}/grocery/b/${businessSlug}/clover/disconnect`,
        null,
        { params: { wipe_data: wipe } },
      );
      const wiped = r.data.wiped || {};
      const wipedMsg = wipe
        ? ` · deleted ${wiped.receipts_deleted || 0} receipts, ${wiped.inventory_deleted || 0} items`
        : "";
      toast.success(`Clover disconnected${wipedMsg}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
    setDisconnecting(false);
  };

  if (loading || !settings || !prefs) {
    return (
      <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
        <CardContent className="p-6 text-sm text-slate-500">Loading settings…</CardContent>
      </Card>
    );
  }

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const heading = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const inputCls = isDark ? "bg-slate-900 border-slate-700 text-white" : "";

  const setAlert = (k, v) => setPrefs({ ...prefs, alerts: { ...prefs.alerts, [k]: v } });
  const setSync = (k, v) => setPrefs({ ...prefs, sync: { ...prefs.sync, [k]: v } });
  const setNotif = (k, v) => setPrefs({ ...prefs, notifications: { ...prefs.notifications, [k]: v } });

  return (
    <div className="space-y-4" data-testid="grocery-settings-root">
      {/* Sticky save bar */}
      {dirty && (
        <div className={`sticky top-0 z-10 flex items-center justify-between px-4 py-2 rounded-lg border ${
          isDark ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"
        }`} data-testid="grocery-settings-dirty-bar">
          <div className="flex items-center gap-2 text-xs">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>You have unsaved changes.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={load} disabled={saving}>Discard</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="grocery-settings-save-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}

      {/* --- Alerts --- */}
      <Card className={cardBg} data-testid="settings-section-alerts">
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${heading} flex items-center gap-2`}>
            <Bell className="w-4 h-4" /> Alert Thresholds
          </CardTitle>
          <p className={`text-xs ${sub}`}>When these thresholds trigger, we surface them on the Dashboard (and via SMS/email when notifications are enabled below).</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className={`text-xs ${sub}`}>Default low-stock threshold</Label>
            <Input type="number" min={1} value={prefs.alerts.low_stock_default_threshold}
              onChange={(e) => setAlert("low_stock_default_threshold", Number(e.target.value))}
              className={inputCls}
              data-testid="setting-low-stock-threshold" />
            <p className={`text-[10px] mt-1 ${sub}`}>Applied to new items — per-item override in Inventory.</p>
          </div>
          <div>
            <Label className={`text-xs ${sub}`}>Expiration warning window (days)</Label>
            <Input type="number" min={1} value={prefs.alerts.expiration_warning_days}
              onChange={(e) => setAlert("expiration_warning_days", Number(e.target.value))}
              className={inputCls}
              data-testid="setting-expiration-days" />
            <p className={`text-[10px] mt-1 ${sub}`}>Items expiring within this window show as "Expiring Soon".</p>
          </div>
          <div>
            <Label className={`text-xs ${sub}`}>High-value sale alert ($)</Label>
            <Input type="number" min={0} value={prefs.alerts.high_value_sale_amount}
              onChange={(e) => setAlert("high_value_sale_amount", Number(e.target.value))}
              className={inputCls}
              data-testid="setting-high-value" />
            <p className={`text-[10px] mt-1 ${sub}`}>Notify when a single transaction crosses this amount.</p>
          </div>
        </CardContent>
      </Card>

      {/* --- Tax (legacy /settings) --- */}
      <Card className={cardBg} data-testid="settings-section-tax">
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${heading} flex items-center gap-2`}>
            <DollarSign className="w-4 h-4" /> Tax Rules
          </CardTitle>
          <p className={`text-xs ${sub}`}>How categories are taxed. Grocery basics are typically untaxed in most US states; prepared food and beverages usually are.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className={`text-xs ${sub}`}>Default sales tax rate (%)</Label>
              <Input type="number" step="0.001" min={0} max={0.5}
                value={settings.tax_rate}
                onChange={(e) => setSettings({ ...settings, tax_rate: Number(e.target.value) })}
                className={inputCls}
                data-testid="setting-tax-rate" />
              <p className={`text-[10px] mt-1 ${sub}`}>Decimal (0.075 = 7.5%)</p>
            </div>
            <div>
              <Label className={`text-xs ${sub}`}>Receipt prefix</Label>
              <Input value={settings.receipt_prefix || ""}
                onChange={(e) => setSettings({ ...settings, receipt_prefix: e.target.value })}
                className={inputCls}
                data-testid="setting-receipt-prefix" />
            </div>
            <div>
              <Label className={`text-xs ${sub}`}>PO prefix</Label>
              <Input value={settings.po_prefix || ""}
                onChange={(e) => setSettings({ ...settings, po_prefix: e.target.value })}
                className={inputCls}
                data-testid="setting-po-prefix" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 pt-1">
            {[
              { key: "tax_groceries", label: "Tax basic groceries" },
              { key: "tax_prepared_food", label: "Tax prepared food" },
              { key: "tax_beverages", label: "Tax sodas/candy/beverages" },
              { key: "ebt_accepted", label: "Accept EBT" },
              { key: "wic_accepted", label: "Accept WIC" },
            ].map((row) => (
              <label key={row.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={!!settings[row.key]}
                  onCheckedChange={(v) => setSettings({ ...settings, [row.key]: v })}
                  data-testid={`setting-${row.key.replace(/_/g, "-")}`} />
                <span className={heading}>{row.label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* --- Sync config --- */}
      <Card className={cardBg} data-testid="settings-section-sync">
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${heading} flex items-center gap-2`}>
            <Clock className="w-4 h-4" /> Clover Sync
          </CardTitle>
          <p className={`text-xs ${sub}`}>Controls how far back we pull historical data on each manual sync. Real-time webhooks (when activated) bypass this window entirely.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className={`text-xs ${sub}`}>Backfill window (days)</Label>
            <Input type="number" min={1} max={365} value={prefs.sync.backfill_days}
              onChange={(e) => setSync("backfill_days", Number(e.target.value))}
              className={inputCls}
              data-testid="setting-backfill-days" />
            <p className={`text-[10px] mt-1 ${sub}`}>Larger = slower syncs but more historical coverage.</p>
          </div>
          <div className="flex items-center justify-between border rounded-md px-3 py-2"
               style={{ borderColor: isDark ? "rgb(51 65 85)" : "rgb(226 232 240)" }}>
            <div>
              <div className={`text-sm ${heading}`}>Auto-sync enabled</div>
              <p className={`text-[10px] ${sub}`}>Runs a small delta sync every 15 minutes.</p>
            </div>
            <Switch checked={prefs.sync.auto_sync_enabled}
              onCheckedChange={(v) => setSync("auto_sync_enabled", v)}
              data-testid="setting-auto-sync" />
          </div>
        </CardContent>
      </Card>

      {/* --- Notifications --- */}
      <Card className={cardBg} data-testid="settings-section-notifications">
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${heading} flex items-center gap-2`}>
            <MessageSquare className="w-4 h-4" /> Notifications
          </CardTitle>
          <p className={`text-xs ${sub}`}>Where alerts get delivered when the thresholds above trigger.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Email */}
            <div className="border rounded-md p-3 space-y-2" style={{ borderColor: isDark ? "rgb(51 65 85)" : "rgb(226 232 240)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className={`w-4 h-4 ${sub}`} />
                  <span className={`text-sm font-medium ${heading}`}>Email</span>
                </div>
                <Switch checked={prefs.notifications.email_enabled}
                  onCheckedChange={(v) => setNotif("email_enabled", v)}
                  data-testid="setting-email-enabled" />
              </div>
              <Input
                placeholder="a@b.com, c@d.com"
                value={(prefs.notifications.email_recipients || []).join(", ")}
                onChange={(e) => setNotif("email_recipients", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                disabled={!prefs.notifications.email_enabled}
                className={inputCls}
                data-testid="setting-email-recipients"
              />
              <p className={`text-[10px] ${sub}`}>Comma-separated. We use Resend to send.</p>
            </div>
            {/* SMS */}
            <div className="border rounded-md p-3 space-y-2" style={{ borderColor: isDark ? "rgb(51 65 85)" : "rgb(226 232 240)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className={`w-4 h-4 ${sub}`} />
                  <span className={`text-sm font-medium ${heading}`}>SMS</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                    Twilio required
                  </span>
                </div>
                <Switch checked={prefs.notifications.sms_enabled}
                  onCheckedChange={(v) => setNotif("sms_enabled", v)}
                  disabled  // will unblock when Twilio keys are wired
                  data-testid="setting-sms-enabled" />
              </div>
              <Input
                placeholder="+15551234567, +15559876543"
                value={(prefs.notifications.sms_recipients || []).join(", ")}
                onChange={(e) => setNotif("sms_recipients", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                disabled
                className={inputCls}
                data-testid="setting-sms-recipients"
              />
              <p className={`text-[10px] ${sub}`}>Add Twilio credentials to enable SMS (contact us).</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- Danger zone --- */}
      <Card className={`${cardBg} border-red-500/20`} data-testid="settings-section-danger">
        <CardHeader className="pb-2">
          <CardTitle className={`text-base text-red-500 flex items-center gap-2`}>
            <Unplug className="w-4 h-4" /> Clover Connection
          </CardTitle>
          <p className={`text-xs ${sub}`}>Disconnect from Clover. Historical data is kept by default — you can reconnect later without losing anything.</p>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => disconnectClover(false)}
            disabled={disconnecting}
            data-testid="clover-disconnect-safe-btn"
          >
            <Unplug className="w-3.5 h-3.5 mr-1.5" />
            Disconnect (keep data)
          </Button>
          <Button
            variant="outline"
            onClick={() => disconnectClover(true)}
            disabled={disconnecting}
            className="border-red-500/40 text-red-500 hover:bg-red-500/10"
            data-testid="clover-disconnect-wipe-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Disconnect + wipe all Clover data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
